import React from "react";
import { SkeletonTableRows } from "../Skeleton/GenericSkeleton";
import type { GenericTableColumn } from "./GenericTable";

/** Below this width an opted-in table renders as cards (Tailwind's `sm`). */
export const MOBILE_CARDS_MAX_WIDTH = 639;

/**
 * Where a card puts each column, by header text. Every slot is optional; a
 * column named in none of them becomes a label/value field.
 */
// Full class names, so Tailwind generates them.
const HEADLINE_CLAMP = {
  1: "truncate [&_*]:truncate",
  2: "line-clamp-2 [overflow-wrap:anywhere]",
  3: "line-clamp-3 [overflow-wrap:anywhere]",
} as const;

export interface MobileCardLayout<T = unknown> {
  /**
   * Heads the card, top left — the row's ID. Defaults to the first column.
   * `null` for no title: a detail table (P2P addressing, BGP summary) has no
   * ID, and its first value shown bare would lose its label.
   */
  titleColumn?: number | string | null;
  /** Top right, beside the title: a short badge such as Type or Status. */
  badgeColumn?: string;
  /** A full-width line under the title, cut to one line: a name or title. */
  headlineColumn?: string;
  /** Lines the headline may take before it is cut: more for long subjects (tickets). */
  headlineLines?: 1 | 2 | 3;
  /** Labelled columns that are controls (e.g. "Details"): top right, after the badge. */
  actionColumns?: string[];
  /** Fields that take the whole row, for long values such as a parent connection. */
  wideColumns?: string[];
  /**
   * Columns closing the card, full width and unlabelled: the row's main action
   * (a report's download), where a thumb finds it.
   */
  footerColumns?: string[];
  /** A larger title and a medium-weight headline, for cards read by who and when (reports). */
  prominent?: boolean;
  /**
   * The only labelled columns shown as fields, when set: a phone card keeps the
   * few a person scans for and leaves the rest to the detail (CTX-1381).
   */
  fieldColumns?: string[];
  /** Columns the card leaves out altogether, such as a quick-view button the card's tap replaces. */
  omitColumns?: string[];
  /**
   * A tap anywhere on the card does what its title does (the ID link that
   * opens the detail), unless it lands on another control in the card.
   */
  opensFromTitle?: boolean;
  /** A row above the cards with the total and a "Sort" picker, for a list long enough to need it. */
  sortControl?: boolean;
  /** What the list counts, singular ("port"): the sort row reads "49 ports". */
  itemNoun?: string;
  /**
   * One unlabelled line under the title, its parts joined by " · " and empty
   * ones dropped: "ACME Corp · KIO QRO1 · 10 Gbps". With it a card is two
   * lines and shows no fields unless `fieldColumns` asks for some (CTX-1381).
   */
  summary?: (row: T) => Array<string | number | null | undefined>;
}

/** The summary line's text, or "" when every part is empty. */
export const cardSummaryText = (parts: Array<string | number | null | undefined>): string =>
  parts
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter((part) => part && part !== "-" && part !== "—")
    .join(" · ");

/**
 * A column earns a label in the card when its header says something. An empty
 * header is an actions or toggle column: it goes top right, like actionColumns.
 */
function hasLabel<T>(col: GenericTableColumn<T>): boolean {
  return typeof col.header === "string" ? col.header.trim() !== "" : Boolean(col.header);
}

function cellKey<T>(col: GenericTableColumn<T>, index: number): string {
  return col.id ?? (typeof col.header === "string" && col.header ? col.header : `col_${index}`);
}

/** A row's raw value for a column, when the column reads one field. */
function rawValue<T>(row: T, col: GenericTableColumn<T>): unknown {
  if (col.accessor) return row[col.accessor];
  return col.sortAccessor?.(row);
}

/** What the tables print for "nothing": a dash of any kind, or no value at all. */
const EMPTY_TEXT = new Set(["", "-", "–", "—"]);

/**
 * Whether a field has nothing to show. A table keeps the dash so its columns
 * line up; a card has no columns, and a stack of "Term -", "Renew Date -" only
 * pushes the content apart. Only columns that read a field can tell; the rest
 * always show.
 */
function isEmptyField<T>(row: T, col: GenericTableColumn<T>): boolean {
  if (!col.accessor && !col.sortAccessor) return false;
  const value = rawValue(row, col);
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === "string" && EMPTY_TEXT.has(value.trim());
}

/**
 * The headline says nothing when it repeats the title — a port named after its
 * own ID is common — so the card drops it then. An empty headline (a tag with
 * no description) is dropped like an empty field.
 */
function repeatsTitle<T>(row: T, headline: GenericTableColumn<T>, title: GenericTableColumn<T>): boolean {
  const value = rawValue(row, headline);
  // Only plain values can repeat: two objects both stringify to "[object Object]".
  if (value === undefined || value === null || typeof value === "object") return false;
  return String(value) === String(rawValue(row, title));
}

function findColumn<T>(columns: GenericTableColumn<T>[], header?: number | string): GenericTableColumn<T> | undefined {
  if (header === undefined) return undefined;
  if (typeof header === "number") return columns[header];
  return columns.find((col) => col.header === header);
}

/**
 * Cells are written for a table cell, and some right-align their content
 * (`justify-end`). In a card every value starts at its label's edge.
 */
const VALUE_ALIGN =
  "[&_.justify-end]:justify-start [&_.justify-center]:justify-start [&_.items-end]:items-start [&_.text-right]:text-left [&_.text-center]:text-left [&_button]:text-left";

export interface GenericTableCardsProps<T> {
  columns: GenericTableColumn<T>[];
  rows: T[];
  /** Index, in the full data, of `rows[0]` — the row index cells are rendered with. */
  startIndex: number;
  layout: MobileCardLayout<T>;
  loading: boolean;
  skeletonRows: number;
  renderCell: (col: GenericTableColumn<T>, row: T, rowIndex: number, isExpanded: boolean) => React.ReactNode;
  rowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, rowIndex: number) => void;
  selectedRowKeys?: Set<string | number>;
  renderRowBelow?: (row: T, rowIndex: number) => React.ReactNode;
  expandable: boolean;
  expandedRows: Set<number>;
  toggleRowExpand: (rowIndex: number) => void;
  renderExpanded?: (row: T) => React.ReactNode;
  renderExpandToggle?: (isExpanded: boolean, onClick: () => void, row: T) => React.ReactNode;
  showExpandColumn: boolean;
}

/**
 * GenericTable's rows as a list of cards, for a phone (CTX-1339). A table on a
 * 390 px screen showed two and a half columns and hid the rest behind a
 * sideways scroll. A card reads top-down instead: ID and badge on the first
 * line, the headline on the second, then the remaining columns as label/value
 * pairs two to a row. Cells are rendered by the same `renderCell` the table
 * uses, so links, badges and menus behave the same.
 */
export function GenericTableCards<T>({
  columns,
  rows,
  startIndex,
  layout,
  loading,
  skeletonRows,
  renderCell,
  rowKey,
  onRowClick,
  selectedRowKeys,
  renderRowBelow,
  expandable,
  expandedRows,
  toggleRowExpand,
  renderExpanded,
  renderExpandToggle,
  showExpandColumn,
}: GenericTableCardsProps<T>) {
  if (loading) {
    return (
      <div className="px-4 py-3">
        <SkeletonTableRows rows={skeletonRows} cols={2} rowHeight={30} animated={true} />
      </div>
    );
  }
  if (rows.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-semantic-text-primary">No Data Available</p>;
  }

  const title = layout.titleColumn === null ? undefined : findColumn(columns, layout.titleColumn) ?? columns[0];
  const badge = findColumn(columns, layout.badgeColumn);
  const headline = findColumn(columns, layout.headlineColumn);
  const actionHeaders = new Set(layout.actionColumns ?? []);
  const isAction = (col: GenericTableColumn<T>) =>
    !hasLabel(col) || (typeof col.header === "string" && actionHeaders.has(col.header));
  const footerHeaders = new Set(layout.footerColumns ?? []);
  const footer = columns.filter((col) => typeof col.header === "string" && footerHeaders.has(col.header));
  const omitted = new Set(layout.omitColumns ?? []);
  const fieldHeaders = layout.fieldColumns
    ? new Set(layout.fieldColumns)
    : layout.summary
      ? new Set<string>()
      : null;
  const isShown = (col: GenericTableColumn<T>) => !(typeof col.header === "string" && omitted.has(col.header));
  const placed = new Set([title, badge, headline, ...footer]);
  const actions = columns.filter((col) => !placed.has(col) && isAction(col) && isShown(col));
  const fields = columns.filter(
    (col) =>
      !placed.has(col) &&
      !isAction(col) &&
      isShown(col) &&
      (!fieldHeaders || (typeof col.header === "string" && fieldHeaders.has(col.header)))
  );
  const wideHeaders = new Set(layout.wideColumns ?? []);
  const isWide = (col: GenericTableColumn<T>) => typeof col.header === "string" && wideHeaders.has(col.header);

  return (
    <ul className="divide-y divide-semantic-border-secondary">
      {rows.map((row, i) => {
        const rowIndex = startIndex + i;
        const isExpanded = expandedRows.has(rowIndex);
        const key = rowKey ? rowKey(row, rowIndex) : rowIndex;
        const below = renderRowBelow?.(row, rowIndex);
        const hasToggle = expandable && showExpandColumn;
        const cell = (col: GenericTableColumn<T>) => renderCell(col, row, rowIndex, isExpanded);
        const rowFields = fields.filter((col) => !isEmptyField(row, col));
        const summaryText = layout.summary ? cardSummaryText(layout.summary(row)) : "";
        return (
          <li
            key={key}
            onClick={(event) => {
              if (onRowClick) {
                onRowClick(row, rowIndex);
                return;
              }
              if (!layout.opensFromTitle) return;
              // A tap on the card's other links and buttons does what they do.
              const target = event.target as HTMLElement;
              if (target.closest("a, button, input, select, textarea, [role='button']")) return;
              event.currentTarget.querySelector<HTMLElement>("[data-card-title] a, [data-card-title] button")?.click();
            }}
            className={`px-4 py-3 transition-colors ${onRowClick || layout.opensFromTitle ? "cursor-pointer hover:bg-semantic-state-active" : ""} ${
              selectedRowKeys?.has(key) ? "bg-semantic-state-active" : "bg-semantic-bg-primary"
            }`}
          >
            {(title || badge || actions.length > 0) && (
              <div className={`${layout.summary ? "mb-1" : "mb-2"} flex items-center justify-between gap-3`}>
                {title && (
                  <div
                    data-card-title
                    className={`min-w-0 truncate font-semibold text-semantic-text-primary ${layout.prominent ? "text-base" : "text-sm"}`}
                  >
                    {cell(title)}
                  </div>
                )}
                {(badge || actions.length > 0) && (
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    {badge && <div className="text-xs">{cell(badge)}</div>}
                    {actions.map((col) => (
                      <div key={cellKey(col, columns.indexOf(col))}>{cell(col)}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {headline && !isEmptyField(row, headline) && !(title && repeatsTitle(row, headline, title)) && (
              <div
                className={`-mt-1 mb-2 text-sm text-semantic-text-primary ${HEADLINE_CLAMP[layout.headlineLines ?? 1]} ${layout.prominent ? "font-medium" : ""}`}
              >
                {cell(headline)}
              </div>
            )}
            {summaryText && (
              <p className="truncate text-sm text-semantic-text-secondary">{summaryText}</p>
            )}
            {rowFields.length > 0 && (
              // Dense flow: a pair field after a wide one backfills the half row it left.
              <dl className="grid grid-flow-row-dense grid-cols-2 gap-x-4 gap-y-2">
                {rowFields.map((col) => (
                  <div key={cellKey(col, columns.indexOf(col))} className={`min-w-0 ${isWide(col) ? "col-span-2" : ""}`}>
                    <dt className="text-xs text-semantic-text-secondary">{col.header}</dt>
                    <dd className={`mt-0.5 min-w-0 break-words text-sm text-semantic-text-primary ${VALUE_ALIGN}`}>
                      {cell(col)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {footer.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {footer.map((col) => (
                  <div key={cellKey(col, columns.indexOf(col))}>{cell(col)}</div>
                ))}
              </div>
            )}
            {below != null && below !== false && (
              <div className="mt-2 text-xs leading-snug text-semantic-text-secondary">{below}</div>
            )}
            {hasToggle && (
              <div className="mt-2 flex justify-end">
                {renderExpandToggle ? (
                  renderExpandToggle(isExpanded, () => toggleRowExpand(rowIndex), row)
                ) : (
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs font-medium hover:bg-semantic-state-active"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRowExpand(rowIndex);
                    }}
                  >
                    {isExpanded ? "–" : "+"}
                  </button>
                )}
              </div>
            )}
            {expandable && isExpanded && renderExpanded && <div className="mt-2">{renderExpanded(row)}</div>}
          </li>
        );
      })}
    </ul>
  );
}
